import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, Loader2 } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { api, apiClient } from '../lib/api'
import { useSortState } from '../lib/useSortState'
import { klassenbuchApi, type ScheduleGrid } from '../lib/klassenbuchApi'
import TimetableUntisImportPanel, {
  type BlockingQuestion,
  type CreatedCourse,
  type ImportSummaryTeacher,
  type LdapCommitTeachers,
  type LdapTeacherPending
} from '../components/timetables/TimetableUntisImportPanel'
import KlassenbuchSettings from '../components/settings/KlassenbuchSettings'
import {
  Badge,
  Button,
  EmptyTableRow,
  ListToolbar,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea,
  SortableTh
} from '../components/ui'

type MetaItem = { id: number; name: string; code?: string; start_date?: string; end_date?: string }
type LocationRow = { id: number; code: string; name: string; is_active: number | boolean }
type SubjectItem = { id: number; subject_name: string; subject_code?: string | null }
type SubjectAliasRow = {
  id: number
  subject_id: number
  alias_code: string
  subject_name: string
  subject_code?: string | null
}
type TimetableRow = {
  id: number
  uid: string
  school_year_id: number
  school_year_name: string
  location_id: number | null
  location_name: string | null
  schedule_grid_id: string | null
  label: string
  status: 'draft' | 'active' | 'archived'
  version_no: number
  valid_from: string
  valid_until: string
  entry_count: number
}

type TeacherMappingRow = {
  id: number
  username: string
  first_name: string
  last_name: string
  email: string | null
  role: string | null
  untis_teacher_code: string | null
  additional_untis_codes?: string[]
}

type UntisTeacherUnlinkedRow = {
  teacher_code: string
  teacher_display_name: string
  entry_count: number
  timetable_labels: string[]
}

type TeacherMappingCounts = {
  lr_linked: number
  lr_unlinked: number
  untis_unlinked: number
}

type TeacherMappingSuggestion = {
  teacher_code: string
  teacher_display_name: string
  entry_count: number
  user_id: number
  username: string
  first_name: string
  last_name: string
  confidence: number
  method: string
  reason: string
}

type TeacherMappingUnresolved = {
  teacher_code: string
  teacher_display_name: string
  entry_count: number
  candidate_count: number
}

const TEACHER_SUGGEST_HIGH_CONFIDENCE = 0.9
const TEACHER_SUGGEST_PRESELECT_CONFIDENCE = 0.85

function teacherSuggestKey(row: { teacher_code: string; user_id: number }): string {
  return `${row.teacher_code}::${row.user_id}`
}

function suggestMethodLabel(method: string): string {
  switch (method) {
    case 'ldap_cn':
      return 'LDAP (Kürzel)'
    case 'ldap_name':
      return 'LDAP (Name)'
    case 'surname_code':
      return 'Nachname = Kürzel'
    case 'display_name':
      return 'Anzeigename'
    case 'hub':
      return 'HUB'
    default:
      return method || '—'
  }
}

type TimetableSortKey =
  | 'label'
  | 'school_year_name'
  | 'location_name'
  | 'schedule_grid'
  | 'status'
  | 'valid_from'
  | 'entry_count'

function scheduleGridLabelForRow(tt: TimetableRow, labelById: Map<string, string>): string {
  return tt.schedule_grid_id ? labelById.get(tt.schedule_grid_id) || tt.schedule_grid_id : 'Standard'
}

function compareTimetableRows(
  a: TimetableRow,
  b: TimetableRow,
  key: TimetableSortKey,
  labelById: Map<string, string>
): number {
  switch (key) {
    case 'schedule_grid':
      return scheduleGridLabelForRow(a, labelById).localeCompare(scheduleGridLabelForRow(b, labelById), 'de', {
        sensitivity: 'base'
      })
    case 'status': {
      const rank: Record<TimetableRow['status'], number> = { active: 0, draft: 1, archived: 2 }
      return rank[a.status] - rank[b.status]
    }
    case 'entry_count':
      return (a.entry_count ?? 0) - (b.entry_count ?? 0)
    case 'location_name':
      return String(a.location_name ?? '').localeCompare(String(b.location_name ?? ''), 'de', { sensitivity: 'base' })
    case 'label':
      return a.label.localeCompare(b.label, 'de', { sensitivity: 'base' })
    case 'school_year_name':
      return a.school_year_name.localeCompare(b.school_year_name, 'de', { sensitivity: 'base' })
    case 'valid_from':
      return String(a.valid_from).localeCompare(String(b.valid_from))
    default:
      return 0
  }
}

function timetableStatusLabel(status: TimetableRow['status']): string {
  if (status === 'active') return 'Aktiv'
  if (status === 'draft') return 'Entwurf'
  return 'Archiviert'
}

function lrRoleLabel(role: string | null | undefined): string {
  const r = String(role || '').toLowerCase()
  if (r === 'teacher' || r === 'lehrer') return 'Lehrkraft'
  if (r === 'klassenlehrer') return 'Klassenlehrer'
  if (r === 'admin') return 'Admin'
  if (r === 'schulleitung') return 'Schulleitung'
  if (r === 'abteilungsleiter' || r === 'abteilungsleitung') return 'Abteilungsleitung'
  return role || '—'
}

const fileFieldLabels: Array<{ key: string; label: string }> = [
  { key: 'gpu001', label: 'GPU001.TXT (Unterricht / Einträge)' },
  { key: 'gpu002', label: 'GPU002.TXT (Unterricht / Metadaten)' },
  { key: 'gpu003', label: 'GPU003.TXT (Klassen)' },
  { key: 'gpu004', label: 'GPU004.TXT (Lehrkräfte)' },
  { key: 'gpu005', label: 'GPU005.TXT (Räume)' },
  { key: 'gpu006', label: 'GPU006.TXT (Fächer)' }
]

function parseApiError(error: any): string {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    'Unbekannter Fehler'
  )
}

function resolutionActionForQuestionType(type: string): string {
  if (type.startsWith('class_')) return 'map_class'
  if (type.startsWith('course_')) return 'map_course'
  if (type === 'teacher_ambiguous') return 'map_teacher'
  return 'skip'
}

const IMPORT_LOG_MAX = 48

function formatUntisImportStreamLine(ev: Record<string, unknown>): string {
  const t = String(ev.type || '')
  if (t === 'phase') return String(ev.message || '…')
  if (t === 'timetable_created') {
    const lbl = String(ev.label || 'Stundenplan')
    const ver = Number(ev.version_no)
    const st = String(ev.status || '')
    const uid = String(ev.timetable_uid || '').slice(0, 8)
    return `Stundenplan „${lbl}“ angelegt (Version ${Number.isFinite(ver) ? ver : '—'}, ${st}${uid ? `, UID ${uid}…` : ''})`
  }
  if (t === 'periods_written') {
    const n = Number(ev.count)
    return `${Number.isFinite(n) ? n : 0} Stunden-Perioden geschrieben`
  }
  if (t === 'course_created') {
    const name = String(ev.name || 'Kurs')
    const key = ev.untis_course_key ? String(ev.untis_course_key) : ''
    return key ? `Kurs „${name}“ angelegt (${key})` : `Kurs „${name}“ angelegt`
  }
  if (t === 'entries_progress') {
    const c = Number(ev.current)
    const tot = Number(ev.total)
    return `Stundenplanzeilen ${Number.isFinite(c) ? c : 0} / ${Number.isFinite(tot) ? tot : 0}`
  }
  if (t === 'ldap_user_created') {
    return `Lehrkraft-Konto „${String(ev.ldap_cn || '')}“ angelegt (UNTIS ${String(ev.teacher_code || '')})`
  }
  if (t === 'ldap_code_mapped') {
    return `UNTIS „${String(ev.teacher_code || '')}“ mit Konto verknüpft (${String(ev.ldap_cn || '')})`
  }
  return ''
}

export default function Timetables() {
  const { user } = useAuth()
  const [schoolYears, setSchoolYears] = useState<MetaItem[]>([])
  const [locations, setLocations] = useState<MetaItem[]>([])
  const [scheduleGrids, setScheduleGrids] = useState<ScheduleGrid[]>([])
  const [defaultScheduleGridId, setDefaultScheduleGridId] = useState<string>('')
  const [timetables, setTimetables] = useState<TimetableRow[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const [selectedSchoolYearId, setSelectedSchoolYearId] = useState<string>('')
  const [selectedLocationId, setSelectedLocationId] = useState<string>('')
  const [selectedScheduleGridId, setSelectedScheduleGridId] = useState<string>('')
  const [label, setLabel] = useState('')
  const [files, setFiles] = useState<Record<string, File | null>>({
    gpu001: null,
    gpu002: null,
    gpu003: null,
    gpu004: null,
    gpu005: null,
    gpu006: null
  })

  const [sessionUid, setSessionUid] = useState('')
  const [blockingQuestions, setBlockingQuestions] = useState<BlockingQuestion[]>([])
  const [resolutions, setResolutions] = useState<Record<string, { action: string; target_id?: number }>>({})
  const [teachersWithoutAccount, setTeachersWithoutAccount] = useState<ImportSummaryTeacher[]>([])
  const [createdCourses, setCreatedCourses] = useState<CreatedCourse[]>([])
  const [ldapDryRunHints, setLdapDryRunHints] = useState<{
    linkedCodes: number
    pendingCreate: LdapTeacherPending[]
  }>({ linkedCodes: 0, pendingCreate: [] })
  const [ldapCommitSummary, setLdapCommitSummary] = useState<LdapCommitTeachers | null>(null)
  const [subjects, setSubjects] = useState<SubjectItem[]>([])
  const [subjectAliases, setSubjectAliases] = useState<SubjectAliasRow[]>([])
  const [selectedAliasSubjectId, setSelectedAliasSubjectId] = useState<string>('')
  const [newAliasCode, setNewAliasCode] = useState('')
  const [showImportModal, setShowImportModal] = useState(false)
  const [timetablePendingDelete, setTimetablePendingDelete] = useState<TimetableRow | null>(null)
  const [deleteTimetableBusy, setDeleteTimetableBusy] = useState(false)
  const [timetableEditTarget, setTimetableEditTarget] = useState<TimetableRow | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [editSchoolYearId, setEditSchoolYearId] = useState('')
  const [editLocationId, setEditLocationId] = useState('')
  const [editScheduleGridId, setEditScheduleGridId] = useState('')
  const [editValidFrom, setEditValidFrom] = useState('')
  const [editValidUntil, setEditValidUntil] = useState('')
  const [editTimetableBusy, setEditTimetableBusy] = useState(false)
  const [allLocations, setAllLocations] = useState<LocationRow[]>([])
  const [locationsManageLoading, setLocationsManageLoading] = useState(false)
  const [newLocationCode, setNewLocationCode] = useState('')
  const [newLocationName, setNewLocationName] = useState('')
  const [locationEditTarget, setLocationEditTarget] = useState<LocationRow | null>(null)
  const [editLocationCode, setEditLocationCode] = useState('')
  const [editLocationName, setEditLocationName] = useState('')
  const [editLocationActive, setEditLocationActive] = useState(true)
  const [locationEditBusy, setLocationEditBusy] = useState(false)
  const [locationCreateBusy, setLocationCreateBusy] = useState(false)
  const [teacherMappingsLinked, setTeacherMappingsLinked] = useState<TeacherMappingRow[]>([])
  const [teacherMappingsLrUnlinked, setTeacherMappingsLrUnlinked] = useState<TeacherMappingRow[]>([])
  const [teacherMappingsUntisUnlinked, setTeacherMappingsUntisUnlinked] = useState<UntisTeacherUnlinkedRow[]>([])
  const [teacherMappingCounts, setTeacherMappingCounts] = useState<TeacherMappingCounts | null>(null)
  const [teacherMappingsLoading, setTeacherMappingsLoading] = useState(false)
  const [teacherMappingsSearch, setTeacherMappingsSearch] = useState('')
  const [teacherEditTarget, setTeacherEditTarget] = useState<TeacherMappingRow | null>(null)
  const [teacherEditCode, setTeacherEditCode] = useState('')
  const [teacherLinkUserId, setTeacherLinkUserId] = useState('')
  const [teacherLinkModalOpen, setTeacherLinkModalOpen] = useState(false)
  const [teacherEditBusy, setTeacherEditBusy] = useState(false)
  const [teacherSuggestModalOpen, setTeacherSuggestModalOpen] = useState(false)
  const [teacherSuggestBusy, setTeacherSuggestBusy] = useState(false)
  const [teacherSuggestApplyBusy, setTeacherSuggestApplyBusy] = useState(false)
  const [teacherSuggestUseHub, setTeacherSuggestUseHub] = useState(false)
  const [teacherSuggestions, setTeacherSuggestions] = useState<TeacherMappingSuggestion[]>([])
  const [teacherSuggestUnresolved, setTeacherSuggestUnresolved] = useState<TeacherMappingUnresolved[]>([])
  const [teacherSuggestCounts, setTeacherSuggestCounts] = useState<{
    suggestions: number
    high_confidence: number
    unresolved: number
  } | null>(null)
  const [teacherSuggestSelected, setTeacherSuggestSelected] = useState<Set<string>>(new Set())
  const [tableSchoolYearId, setTableSchoolYearId] = useState<string>('')
  const [tableLocationId, setTableLocationId] = useState<string>('')
  const [tableStatus, setTableStatus] = useState<string>('')

  const [untisImportBusy, setUntisImportBusy] = useState(false)
  const [untisImportMode, setUntisImportMode] = useState<'idle' | 'dry_run' | 'commit'>('idle')
  const [importStatusLines, setImportStatusLines] = useState<string[]>([])
  const importLogEndRef = useRef<HTMLDivElement | null>(null)

  const canDryRun = useMemo(() => {
    return (
      selectedSchoolYearId !== '' &&
      selectedScheduleGridId !== '' &&
      fileFieldLabels.every((field) => !!files[field.key])
    )
  }, [files, selectedSchoolYearId, selectedScheduleGridId])

  const scheduleGridLabelById = useMemo(() => {
    const map = new Map<string, string>()
    scheduleGrids.forEach((grid) => map.set(grid.id, grid.name))
    return map
  }, [scheduleGrids])

  const { sortKey, direction, setSort } = useSortState<TimetableSortKey>(null)

  const sortedTimetables = useMemo(() => {
    if (sortKey == null) return [...timetables]
    const mul = direction === 'asc' ? 1 : -1
    return [...timetables].sort(
      (a, b) => mul * compareTimetableRows(a, b, sortKey, scheduleGridLabelById)
    )
  }, [timetables, sortKey, direction, scheduleGridLabelById])

  const unresolvedCount = useMemo(() => {
    return blockingQuestions.filter((question) => {
      const resolution = resolutions[question.id]
      if (!resolution) return true
      if (resolution.action === 'keep_unlinked' || resolution.action === 'skip' || resolution.action === 'create_course') return false
      return !resolution.target_id
    }).length
  }, [blockingQuestions, resolutions])

  const llmTeacherSuggestionCount = useMemo(() => {
    return blockingQuestions.filter(
      (question) =>
        question.type === 'teacher_ambiguous' &&
        Number(question.llm_suggestion?.target_id) > 0
    ).length
  }, [blockingQuestions])

  const unresolvedCourseNotFoundCount = useMemo(() => {
    return blockingQuestions.filter((question) => {
      if (question.type !== 'course_not_found') return false
      const resolution = resolutions[question.id]
      return !(resolution && (resolution.action === 'create_course' || resolution.action === 'skip'))
    }).length
  }, [blockingQuestions, resolutions])

  const roleValues = useMemo(
    () => [
      user?.role,
      ...(Array.isArray(user?.roles) ? user.roles : []),
      ...(Array.isArray(user?.allRoles) ? user.allRoles : [])
    ].map((v) => String(v || '').toLowerCase()),
    [user]
  )

  const isAdmin = useMemo(() => roleValues.includes('admin'), [roleValues])

  const canManageTeacherMappings = useMemo(
    () => roleValues.includes('admin') || roleValues.includes('schulleitung'),
    [roleValues]
  )

  const lrTeachersForLinkSelect = useMemo(
    () => [...teacherMappingsLrUnlinked, ...teacherMappingsLinked],
    [teacherMappingsLrUnlinked, teacherMappingsLinked]
  )

  useEffect(() => {
    if (!untisImportBusy) return
    importLogEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [importStatusLines, untisImportBusy])

  const loadMetaAndList = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [metaRes, listRes, cfgDoc] = await Promise.all([
        api.get('/timetables/meta'),
        api.get('/timetables', {
          params: {
            school_year_id: tableSchoolYearId || undefined,
            location_id: tableLocationId || undefined,
            status: tableStatus || undefined
          }
        }),
        klassenbuchApi.getConfig()
      ])
      setSchoolYears(metaRes.data?.data?.schoolYears || [])
      setLocations(metaRes.data?.data?.locations || [])
      setTimetables(listRes.data?.data || [])
      setScheduleGrids(cfgDoc.grids || [])
      setDefaultScheduleGridId(cfgDoc.defaultGridId || '')
      if (!selectedScheduleGridId) {
        setSelectedScheduleGridId(cfgDoc.defaultGridId || cfgDoc.grids?.[0]?.id || '')
      }
      if (!selectedSchoolYearId && (metaRes.data?.data?.schoolYears || []).length > 0) {
        setSelectedSchoolYearId(String(metaRes.data.data.schoolYears[0].id))
      }
    } catch (err: any) {
      setError(parseApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  const loadAllLocations = async () => {
    if (!isAdmin) return
    setLocationsManageLoading(true)
    try {
      const res = await api.get('/school-locations')
      setAllLocations(res.data?.data || [])
    } catch (err: any) {
      setError((prev) => prev ?? parseApiError(err))
    } finally {
      setLocationsManageLoading(false)
    }
  }

  const loadSubjectAliases = async () => {
    try {
      const res = await api.get('/timetables/subject-aliases')
      const rows = res.data?.data || {}
      setSubjects(rows.subjects || [])
      setSubjectAliases(rows.aliases || [])
      if (!selectedAliasSubjectId && (rows.subjects || []).length > 0) {
        setSelectedAliasSubjectId(String(rows.subjects[0].id))
      }
    } catch (err: any) {
      setError(parseApiError(err))
    }
  }

  useEffect(() => {
    void loadMetaAndList()
  }, [tableSchoolYearId, tableLocationId, tableStatus])

  useEffect(() => {
    void loadSubjectAliases()
  }, [])

  useEffect(() => {
    if (isAdmin) void loadAllLocations()
  }, [isAdmin])

  const loadTeacherMappings = async () => {
    setTeacherMappingsLoading(true)
    try {
      const res = await api.get('/timetables/teacher-mapping-overview', {
        params: { search: teacherMappingsSearch || undefined }
      })
      const data = res.data?.data ?? {}
      const mapLr = (row: any): TeacherMappingRow => ({
        id: Number(row.id),
        username: String(row.username || ''),
        first_name: String(row.first_name || ''),
        last_name: String(row.last_name || ''),
        email: row.email ? String(row.email) : null,
        role: row.role ? String(row.role) : null,
        untis_teacher_code: row.untis_teacher_code ? String(row.untis_teacher_code) : null,
        additional_untis_codes: Array.isArray(row.additional_untis_codes)
          ? row.additional_untis_codes.map(String)
          : []
      })
      setTeacherMappingsLinked((data.lr_teachers_linked || []).map(mapLr))
      setTeacherMappingsLrUnlinked((data.lr_teachers_unlinked || []).map(mapLr))
      setTeacherMappingsUntisUnlinked(
        (data.untis_teachers_unlinked || []).map((row: any) => ({
          teacher_code: String(row.teacher_code || ''),
          teacher_display_name: String(row.teacher_display_name || row.teacher_code || ''),
          entry_count: Number(row.entry_count) || 0,
          timetable_labels: Array.isArray(row.timetable_labels) ? row.timetable_labels.map(String) : []
        }))
      )
      setTeacherMappingCounts(data.counts ?? null)
    } catch (err: any) {
      setError((prev) => prev ?? parseApiError(err))
    } finally {
      setTeacherMappingsLoading(false)
    }
  }

  const handleAddSubjectAlias = async () => {
    if (!selectedAliasSubjectId || !newAliasCode.trim()) return
    setError(null)
    setSuccessMessage(null)
    try {
      await api.post('/timetables/subject-aliases', {
        subject_id: Number(selectedAliasSubjectId),
        alias_code: newAliasCode.trim()
      })
      setNewAliasCode('')
      await loadSubjectAliases()
      setSuccessMessage('Fach-Alias gespeichert.')
    } catch (err: any) {
      setError(parseApiError(err))
    }
  }

  const handleDeleteSubjectAlias = async (aliasId: number) => {
    setError(null)
    setSuccessMessage(null)
    try {
      await api.delete(`/timetables/subject-aliases/${aliasId}`)
      await loadSubjectAliases()
      setSuccessMessage('Fach-Alias gelöscht.')
    } catch (err: any) {
      setError(parseApiError(err))
    }
  }

  const handleOpenTeacherEdit = (row: TeacherMappingRow, presetCode?: string) => {
    setTeacherEditTarget(row)
    setTeacherEditCode(presetCode ?? row.untis_teacher_code ?? '')
    setTeacherLinkUserId(String(row.id))
    setTeacherLinkModalOpen(true)
  }

  const handleOpenUntisLink = (untis: UntisTeacherUnlinkedRow) => {
    setTeacherEditTarget(null)
    setTeacherEditCode(untis.teacher_code)
    setTeacherLinkUserId('')
    setTeacherLinkModalOpen(true)
  }

  const handleCloseTeacherEditModal = () => {
    if (teacherEditBusy) return
    setTeacherLinkModalOpen(false)
    setTeacherEditTarget(null)
    setTeacherEditCode('')
    setTeacherLinkUserId('')
  }

  const isUntisLinkMode = teacherLinkModalOpen && teacherEditTarget === null

  const closeTeacherSuggestModal = () => {
    if (teacherSuggestBusy || teacherSuggestApplyBusy) return
    setTeacherSuggestModalOpen(false)
    setTeacherSuggestions([])
    setTeacherSuggestUnresolved([])
    setTeacherSuggestCounts(null)
    setTeacherSuggestSelected(new Set())
  }

  const runTeacherSuggest = async (useHub: boolean) => {
    setTeacherSuggestBusy(true)
    setError(null)
    setSuccessMessage(null)
    try {
      const res = await api.post('/timetables/teacher-mapping-suggest', {
        search: teacherMappingsSearch || undefined,
        use_hub: useHub
      })
      const data = res.data?.data ?? {}
      const list: TeacherMappingSuggestion[] = Array.isArray(data.suggestions) ? data.suggestions : []
      setTeacherSuggestions(list)
      setTeacherSuggestUnresolved(Array.isArray(data.unresolved) ? data.unresolved : [])
      setTeacherSuggestCounts(data.counts ?? null)
      const preselected = new Set(
        list
          .filter((row) => row.confidence >= TEACHER_SUGGEST_PRESELECT_CONFIDENCE)
          .map((row) => teacherSuggestKey(row))
      )
      setTeacherSuggestSelected(preselected)
      setTeacherSuggestModalOpen(true)
      if (list.length === 0) {
        setSuccessMessage('Keine automatischen Zuordnungsvorschläge gefunden.')
      }
    } catch (err: any) {
      setError(parseApiError(err))
    } finally {
      setTeacherSuggestBusy(false)
    }
  }

  const toggleTeacherSuggestRow = (key: string, checked: boolean) => {
    setTeacherSuggestSelected((prev) => {
      const next = new Set(prev)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }

  const applyTeacherSuggestions = async (keys: Set<string>) => {
    const items = teacherSuggestions
      .filter((row) => keys.has(teacherSuggestKey(row)))
      .map((row) => ({ teacher_code: row.teacher_code, user_id: row.user_id }))
    if (items.length === 0) return
    setTeacherSuggestApplyBusy(true)
    setError(null)
    setSuccessMessage(null)
    try {
      const res = await api.post('/timetables/teacher-mapping-apply', { items })
      const data = res.data?.data ?? {}
      const applied = Number(data.counts?.applied ?? data.applied?.length ?? 0)
      const failed = Number(data.counts?.failed ?? data.failed?.length ?? 0)
      if (applied > 0) {
        setSuccessMessage(
          failed > 0
            ? `${applied} Zuordnung(en) gespeichert, ${failed} fehlgeschlagen.`
            : `${applied} Zuordnung(en) gespeichert.`
        )
      } else if (failed > 0) {
        setError('Zuordnungen konnten nicht gespeichert werden.')
      }
      closeTeacherSuggestModal()
      await loadTeacherMappings()
    } catch (err: any) {
      setError(parseApiError(err))
    } finally {
      setTeacherSuggestApplyBusy(false)
    }
  }

  const handleSaveTeacherEdit = async () => {
    const userId = teacherEditTarget?.id ?? Number(teacherLinkUserId)
    if (!Number.isFinite(userId) || userId <= 0) return
    setTeacherEditBusy(true)
    setError(null)
    setSuccessMessage(null)
    try {
      await api.put(`/users/${userId}`, {
        untis_teacher_code: teacherEditCode.trim() || null
      })
      handleCloseTeacherEditModal()
      setSuccessMessage('UNTIS-Kürzel für Lehrkraft aktualisiert.')
      await loadTeacherMappings()
    } catch (err: any) {
      setError(parseApiError(err))
    } finally {
      setTeacherEditBusy(false)
    }
  }

  const applyLlmTeacherSuggestions = () => {
    const updates: Record<string, { action: string; target_id?: number }> = {}
    blockingQuestions.forEach((question) => {
      if (question.type !== 'teacher_ambiguous') return
      const targetId = Number(question.llm_suggestion?.target_id)
      if (!Number.isFinite(targetId) || targetId <= 0) return
      updates[question.id] = { action: 'map_teacher', target_id: targetId }
    })
    if (Object.keys(updates).length === 0) return
    setResolutions((prev) => ({ ...prev, ...updates }))
    setSuccessMessage('HUB-Vorschläge für Lehrkräfte wurden als Zuordnung vorausgewählt.')
  }

  const applyCreateCourseResolutions = () => {
    const updates: Record<string, { action: string; target_id?: number }> = {}
    blockingQuestions.forEach((question) => {
      if (question.type !== 'course_not_found') return
      updates[question.id] = { action: 'create_course' }
    })
    if (Object.keys(updates).length === 0) return
    setResolutions((prev) => ({ ...prev, ...updates }))
    setSuccessMessage('Alle fehlenden Kurse sind auf "Kurs anlegen" gesetzt.')
  }

  const updateResolution = (questionId: string, action: string, targetId?: number) => {
    setResolutions((prev) => ({
      ...prev,
      [questionId]: targetId ? { action, target_id: targetId } : { action }
    }))
  }

  const handleFileChange = (key: string, file: File | null) => {
    setFiles((prev) => ({ ...prev, [key]: file }))
  }

  const appendImportLog = (line: string) => {
    if (!line) return
    setImportStatusLines((prev) => {
      const next = [...prev, line]
      if (next.length > IMPORT_LOG_MAX) return next.slice(-IMPORT_LOG_MAX)
      return next
    })
  }

  const handleDryRun = async () => {
    setError(null)
    setSuccessMessage(null)
    setUntisImportBusy(true)
    setUntisImportMode('dry_run')
    setImportStatusLines(['UNTIS-Dateien werden eingelesen und geprüft…'])
    const formData = new FormData()
    fileFieldLabels.forEach((field) => {
      if (files[field.key]) formData.append(field.key, files[field.key] as File)
    })
    formData.append('school_year_id', selectedSchoolYearId)
    if (selectedLocationId) formData.append('location_id', selectedLocationId)
    if (selectedScheduleGridId) formData.append('schedule_grid_id', selectedScheduleGridId)
    if (label.trim()) formData.append('label', label.trim())
    try {
      const res = await api.post('/timetables/import-untis/dry-run', formData)
      const data = res.data?.data
      setSessionUid(data?.session_uid || '')
      setBlockingQuestions(data?.blocking_questions || [])
      setTeachersWithoutAccount(data?.import_info?.teachers_without_lr_account || [])
      setLdapDryRunHints({
        linkedCodes: Number(data?.import_info?.ldap_teacher_codes_linked_from_existing) || 0,
        pendingCreate: Array.isArray(data?.import_info?.ldap_teachers_pending_create)
          ? data.import_info.ldap_teachers_pending_create
          : []
      })
      setCreatedCourses([])
      setLdapCommitSummary(null)
      setResolutions({})
      setSuccessMessage('Dry-Run erfolgreich erstellt.')
    } catch (err: any) {
      setError(parseApiError(err))
    } finally {
      setUntisImportBusy(false)
      setUntisImportMode('idle')
      setImportStatusLines([])
    }
  }

  const handleCommit = async (activate: boolean) => {
    if (!sessionUid) return
    setError(null)
    setSuccessMessage(null)
    setUntisImportBusy(true)
    setUntisImportMode('commit')
    setImportStatusLines(['Import wird gestartet…'])

    const resolutionPayload = Object.entries(resolutions).map(([question_id, value]) => ({
      question_id,
      action: value.action,
      target_id: value.target_id
    }))

    const base = apiClient.getBaseUrl().replace(/\/$/, '')
    const url = `${base}/timetables/import-untis/commit?stream=1`

    const processStreamEvent = (ev: Record<string, unknown>) => {
      const line = formatUntisImportStreamLine(ev)
      if (line) appendImportLog(line)
    }

    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/x-ndjson, application/json'
        },
        body: JSON.stringify({
          session_uid: sessionUid,
          resolutions: resolutionPayload,
          activate,
          stream: true
        })
      })

      const ct = res.headers.get('content-type') || ''

      if (ct.includes('application/json')) {
        const j = await res.json()
        if (!res.ok || j.success === false) {
          const blocking = j.data?.blocking_questions
          if (Array.isArray(blocking)) setBlockingQuestions(blocking)
          throw new Error(String(j.error || j.message || 'Import fehlgeschlagen'))
        }
        const d = j.data
        const importSummary = d?.import_summary?.teachers_without_lr_account || []
        setTeachersWithoutAccount(importSummary)
        setCreatedCourses(d?.import_summary?.created_courses || [])
        const lt = d?.import_summary?.ldap_teachers
        setLdapCommitSummary(
          lt && (Array.isArray(lt.users_created) || Array.isArray(lt.additional_untis_codes_mapped))
            ? {
                users_created: lt.users_created || [],
                additional_untis_codes_mapped: lt.additional_untis_codes_mapped || []
              }
            : null
        )
        setBlockingQuestions([])
        setSessionUid('')
        setSuccessMessage(activate ? 'Stundenplan importiert und aktiviert.' : 'Stundenplan als Entwurf importiert.')
        await loadMetaAndList()
        return
      }

      if (!res.ok) {
        const t = await res.text()
        throw new Error(t.slice(0, 400) || `HTTP ${res.status}`)
      }

      const reader = res.body?.getReader()
      if (!reader) throw new Error('Streaming wird von diesem Browser nicht unterstützt.')

      const dec = new TextDecoder()
      let buf = ''
      let finalData: any = null

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += dec.decode(value, { stream: true })
        const lines = buf.split('\n')
        buf = lines.pop() || ''
        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed) continue
          let ev: Record<string, unknown>
          try {
            ev = JSON.parse(trimmed) as Record<string, unknown>
          } catch {
            continue
          }
          const typ = String(ev.type || '')
          if (typ === 'complete') {
            finalData = ev.data
            continue
          }
          if (typ === 'blocked') {
            const blocking = (ev.data as any)?.blocking_questions
            if (Array.isArray(blocking)) setBlockingQuestions(blocking)
            throw new Error('Offene Konflikte vorhanden — bitte Zuordnungen vervollständigen.')
          }
          if (typ === 'error') {
            throw new Error(String(ev.message || 'Import fehlgeschlagen'))
          }
          processStreamEvent(ev)
        }
      }

      if (!finalData) throw new Error('Import: keine Abschlussmeldung vom Server.')

      const importSummary = finalData.import_summary?.teachers_without_lr_account || []
      setTeachersWithoutAccount(importSummary)
      setCreatedCourses(finalData.import_summary?.created_courses || [])
      const lt = finalData.import_summary?.ldap_teachers
      setLdapCommitSummary(
        lt && (Array.isArray(lt.users_created) || Array.isArray(lt.additional_untis_codes_mapped))
          ? {
              users_created: lt.users_created || [],
              additional_untis_codes_mapped: lt.additional_untis_codes_mapped || []
            }
          : null
      )
      setBlockingQuestions([])
      setSessionUid('')
      appendImportLog(activate ? 'Import abgeschlossen — Stundenplan ist aktiv.' : 'Import abgeschlossen — Entwurf gespeichert.')
      setSuccessMessage(activate ? 'Stundenplan importiert und aktiviert.' : 'Stundenplan als Entwurf importiert.')
      await loadMetaAndList()
    } catch (err: any) {
      const blocking = err?.blocking ?? err?.response?.data?.data?.blocking_questions
      if (Array.isArray(blocking)) setBlockingQuestions(blocking)
      setError(err?.message || parseApiError(err))
    } finally {
      setUntisImportBusy(false)
      setUntisImportMode('idle')
      setImportStatusLines([])
    }
  }

  const handleActivate = async (id: number) => {
    setError(null)
    try {
      await api.post(`/timetables/${id}/activate`)
      await loadMetaAndList()
    } catch (err: any) {
      setError(parseApiError(err))
    }
  }

  const handleArchive = async (id: number) => {
    setError(null)
    try {
      await api.post(`/timetables/${id}/archive`)
      await loadMetaAndList()
    } catch (err: any) {
      setError(parseApiError(err))
    }
  }

  const handleConfirmDeleteTimetable = async () => {
    if (!timetablePendingDelete) return
    setError(null)
    setSuccessMessage(null)
    setDeleteTimetableBusy(true)
    try {
      await api.delete(`/timetables/${timetablePendingDelete.id}`)
      setTimetablePendingDelete(null)
      setSuccessMessage('Stundenplan wurde gelöscht.')
      await loadMetaAndList()
    } catch (err: any) {
      setError(parseApiError(err))
    } finally {
      setDeleteTimetableBusy(false)
    }
  }

  const handleOpenTimetableEdit = (row: TimetableRow) => {
    setTimetableEditTarget(row)
    setEditLabel(row.label)
    setEditSchoolYearId(String(row.school_year_id))
    setEditLocationId(row.location_id != null ? String(row.location_id) : '')
    setEditScheduleGridId(row.schedule_grid_id || defaultScheduleGridId || scheduleGrids[0]?.id || '')
    setEditValidFrom(String(row.valid_from).slice(0, 10))
    setEditValidUntil(String(row.valid_until).slice(0, 10))
    setError(null)
    setSuccessMessage(null)
  }

  const handleCloseTimetableEdit = () => {
    if (editTimetableBusy) return
    setTimetableEditTarget(null)
  }

  const handleSaveTimetableEdit = async () => {
    if (!timetableEditTarget) return
    if (!editLabel.trim() || !editSchoolYearId || !editScheduleGridId || !editValidFrom || !editValidUntil) return
    setEditTimetableBusy(true)
    setError(null)
    setSuccessMessage(null)
    try {
      await api.patch(`/timetables/${timetableEditTarget.id}`, {
        label: editLabel.trim(),
        school_year_id: Number(editSchoolYearId),
        location_id: editLocationId || null,
        schedule_grid_id: editScheduleGridId,
        valid_from: editValidFrom,
        valid_until: editValidUntil
      })
      setTimetableEditTarget(null)
      setSuccessMessage('Stundenplan wurde aktualisiert.')
      await loadMetaAndList()
    } catch (err: any) {
      setError(parseApiError(err))
    } finally {
      setEditTimetableBusy(false)
    }
  }

  const handleCreateLocation = async () => {
    if (!newLocationCode.trim() || !newLocationName.trim()) return
    setLocationCreateBusy(true)
    setError(null)
    setSuccessMessage(null)
    try {
      await api.post('/school-locations', {
        code: newLocationCode.trim(),
        name: newLocationName.trim()
      })
      setNewLocationCode('')
      setNewLocationName('')
      setSuccessMessage('Standort wurde angelegt.')
      await Promise.all([loadAllLocations(), loadMetaAndList()])
    } catch (err: any) {
      setError(parseApiError(err))
    } finally {
      setLocationCreateBusy(false)
    }
  }

  const handleOpenLocationEdit = (row: LocationRow) => {
    setLocationEditTarget(row)
    setEditLocationCode(row.code)
    setEditLocationName(row.name)
    setEditLocationActive(Boolean(row.is_active))
    setError(null)
    setSuccessMessage(null)
  }

  const handleCloseLocationEdit = () => {
    if (locationEditBusy) return
    setLocationEditTarget(null)
  }

  const handleSaveLocationEdit = async () => {
    if (!locationEditTarget) return
    if (!editLocationCode.trim() || !editLocationName.trim()) return
    setLocationEditBusy(true)
    setError(null)
    setSuccessMessage(null)
    try {
      await api.put(`/school-locations/${locationEditTarget.id}`, {
        code: editLocationCode.trim(),
        name: editLocationName.trim(),
        is_active: editLocationActive
      })
      setLocationEditTarget(null)
      setSuccessMessage('Standort wurde aktualisiert.')
      await Promise.all([loadAllLocations(), loadMetaAndList()])
    } catch (err: any) {
      setError(parseApiError(err))
    } finally {
      setLocationEditBusy(false)
    }
  }

  const handleCloseImportModal = () => {
    if (untisImportBusy) return
    setShowImportModal(false)
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 pb-8">
      <ListToolbar
        title="Stundenpläne"
        description="Übersicht, Aktivierung und UNTIS-Import."
        trailing={
          <>
            <Button variant="outline" onClick={() => void loadMetaAndList()} disabled={isLoading}>
              Aktualisieren
            </Button>
            <Button onClick={() => setShowImportModal(true)}>UNTIS-Import</Button>
          </>
        }
      />

      {error && !showImportModal && !timetableEditTarget && !locationEditTarget && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </p>
      )}
      {successMessage && !showImportModal && (
        <p className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800 dark:border-green-900/50 dark:bg-green-950/40 dark:text-green-200">
          {successMessage}
        </p>
      )}

      <section className="card p-0 overflow-hidden">
        <div className="border-b border-gray-200 bg-gray-50/80 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Aktivieren, bearbeiten, deaktivieren (archivieren), löschen oder im Viewer öffnen. Klick auf die Spaltenüberschriften sortiert die Liste.
            </p>
            {isLoading && (
              <span className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                Laden…
              </span>
            )}
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="text-sm text-gray-700 dark:text-gray-300">
              Schuljahr
              <select
                className="input-field mt-1"
                value={tableSchoolYearId}
                onChange={(event) => setTableSchoolYearId(event.target.value)}
              >
                <option value="">Alle</option>
                {schoolYears.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-700 dark:text-gray-300">
              Standort
              <select
                className="input-field mt-1"
                value={tableLocationId}
                onChange={(event) => setTableLocationId(event.target.value)}
              >
                <option value="">Alle</option>
                <option value="null">Nur schulweit</option>
                {locations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-700 dark:text-gray-300">
              Status
              <select
                className="input-field mt-1"
                value={tableStatus}
                onChange={(event) => setTableStatus(event.target.value)}
              >
                <option value="">Alle</option>
                <option value="active">Aktiv</option>
                <option value="draft">Entwurf</option>
                <option value="archived">Archiviert</option>
              </select>
            </label>
            <div className="flex items-end">
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => {
                  setTableSchoolYearId('')
                  setTableLocationId('')
                  setTableStatus('')
                }}
              >
                Filter zurücksetzen
              </Button>
            </div>
          </div>
        </div>
        <div className="p-0">
          <TableScrollArea>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTh sortKey="label" activeKey={sortKey} direction={direction} onSort={setSort}>
                    Bezeichnung
                  </SortableTh>
                  <SortableTh sortKey="school_year_name" activeKey={sortKey} direction={direction} onSort={setSort}>
                    Schuljahr
                  </SortableTh>
                  <SortableTh sortKey="location_name" activeKey={sortKey} direction={direction} onSort={setSort}>
                    Standort
                  </SortableTh>
                  <SortableTh sortKey="schedule_grid" activeKey={sortKey} direction={direction} onSort={setSort}>
                    Zeitraster
                  </SortableTh>
                  <SortableTh sortKey="status" activeKey={sortKey} direction={direction} onSort={setSort}>
                    Status
                  </SortableTh>
                  <SortableTh sortKey="valid_from" activeKey={sortKey} direction={direction} onSort={setSort}>
                    Gültig (ab)
                  </SortableTh>
                  <SortableTh
                    sortKey="entry_count"
                    activeKey={sortKey}
                    direction={direction}
                    onSort={setSort}
                    className="text-right"
                  >
                    Einträge
                  </SortableTh>
                  <TableHeaderCell className="text-right">Aktionen</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timetables.length === 0 ? (
                  <EmptyTableRow colSpan={8} title="Keine Stundenpläne vorhanden" />
                ) : (
                  sortedTimetables.map((tt) => (
                    <TableRow key={tt.id}>
                      <TableCell className="max-w-[18rem] whitespace-normal">
                        {tt.label} (v{tt.version_no})
                      </TableCell>
                      <TableCell>{tt.school_year_name}</TableCell>
                      <TableCell>{tt.location_name || 'Schulweit'}</TableCell>
                      <TableCell>{scheduleGridLabelForRow(tt, scheduleGridLabelById)}</TableCell>
                      <TableCell>
                        <Badge
                          variant={tt.status === 'active' ? 'success' : tt.status === 'draft' ? 'warning' : 'neutral'}
                        >
                          {timetableStatusLabel(tt.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {tt.valid_from} bis {tt.valid_until}
                      </TableCell>
                      <TableCell align="right">{tt.entry_count}</TableCell>
                      <TableCell align="right">
                        <div className="flex max-w-[22rem] flex-wrap justify-end gap-2">
                          <Link to={`/timetables/${tt.id}/viewer`}>
                            <Button size="sm" variant="outline">
                              Anzeigen
                            </Button>
                          </Link>
                          <Button size="sm" variant="outline" onClick={() => handleOpenTimetableEdit(tt)}>
                            Bearbeiten
                          </Button>
                          {tt.status !== 'active' && (
                            <Button size="sm" variant="outline" onClick={() => void handleActivate(tt.id)}>
                              Aktivieren
                            </Button>
                          )}
                          {tt.status !== 'archived' && (
                            <Button size="sm" variant="secondary" onClick={() => void handleArchive(tt.id)}>
                              Deaktivieren
                            </Button>
                          )}
                          {tt.status !== 'active' && (
                            <Button size="sm" variant="danger" onClick={() => setTimetablePendingDelete(tt)}>
                              Löschen
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableScrollArea>
        </div>
      </section>

      {canManageTeacherMappings && (
        <details className="group overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-950/30">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-gray-900 outline-none ring-primary-500 hover:bg-gray-50 focus-visible:ring-2 dark:text-gray-100 dark:hover:bg-gray-900/60 [&::-webkit-details-marker]:hidden">
            <span>Lehrkraft-Zuordnung (UNTIS ↔ Lernrakete)</span>
            <span className="flex items-center gap-2 text-xs font-normal text-gray-500 dark:text-gray-400">
              {teacherMappingCounts
                ? `${teacherMappingCounts.lr_unlinked} LR ohne · ${teacherMappingCounts.untis_unlinked} UNTIS ohne`
                : 'Übersicht & Zuordnung'}
              <ChevronDown
                className="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-180 dark:text-gray-500"
                aria-hidden
              />
            </span>
          </summary>
          <div className="space-y-5 border-t border-gray-200 p-4 dark:border-gray-700">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Prüfe fehlende Verknüpfungen — Lehrkraft, Klassenlehrer, Admin, Schulleitung u. a. (auch LDAP).
                UNTIS-Kürzel aus allen Stundenplan-Status. Bearbeiten nur für Admins.
              </p>
              <div className="flex flex-wrap items-end gap-2">
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Suche
                  <input
                    className="input-field mt-1 w-full min-w-[10rem]"
                    placeholder="Name, Kürzel, E-Mail"
                    value={teacherMappingsSearch}
                    onChange={(event) => setTeacherMappingsSearch(event.target.value)}
                  />
                </label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void loadTeacherMappings()}
                  disabled={teacherMappingsLoading}
                >
                  {teacherMappingsLoading ? 'Lädt…' : 'Übersicht laden'}
                </Button>
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void runTeacherSuggest(teacherSuggestUseHub)}
                    disabled={teacherMappingsLoading || teacherSuggestBusy}
                  >
                    {teacherSuggestBusy ? (
                      <>
                        <Loader2 className="mr-1 inline h-4 w-4 animate-spin" aria-hidden />
                        Analysiere…
                      </>
                    ) : (
                      'Zuordnung vorschlagen'
                    )}
                  </Button>
                )}
              </div>
            </div>

            {teacherMappingCounts && (
              <div className="flex flex-wrap gap-2">
                <Badge variant="success">{teacherMappingCounts.lr_linked} verknüpft (LR)</Badge>
                <Badge variant="warning">{teacherMappingCounts.lr_unlinked} LR ohne UNTIS</Badge>
                <Badge variant="danger">{teacherMappingCounts.untis_unlinked} UNTIS ohne LR</Badge>
              </div>
            )}

            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                UNTIS-Lehrkräfte ohne LR-Konto
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Kürzel aus allen importierten Stundenplänen ohne LR-Zuordnung (weder Map noch Primär-Kürzel am Benutzer).
              </p>
              <TableScrollArea>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>UNTIS-Kürzel</TableHeaderCell>
                      <TableHeaderCell>Anzeigename (UNTIS)</TableHeaderCell>
                      <TableHeaderCell align="right">Stunden</TableHeaderCell>
                      <TableHeaderCell>Stundenpläne</TableHeaderCell>
                      {isAdmin && <TableHeaderCell align="right">Aktion</TableHeaderCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teacherMappingsUntisUnlinked.length === 0 ? (
                      <EmptyTableRow
                        colSpan={isAdmin ? 5 : 4}
                        title={
                          teacherMappingCounts
                            ? 'Keine offenen UNTIS-Lehrkräfte'
                            : 'Übersicht laden, um UNTIS-Lehrkräfte ohne LR-Konto zu sehen'
                        }
                      />
                    ) : (
                      teacherMappingsUntisUnlinked.map((row) => (
                        <TableRow key={row.teacher_code}>
                          <TableCell className="font-mono text-sm">{row.teacher_code}</TableCell>
                          <TableCell>{row.teacher_display_name}</TableCell>
                          <TableCell align="right">{row.entry_count}</TableCell>
                          <TableCell className="max-w-[14rem] whitespace-normal text-sm text-gray-600 dark:text-gray-400">
                            {row.timetable_labels.length > 0 ? row.timetable_labels.join(', ') : '—'}
                          </TableCell>
                          {isAdmin && (
                            <TableCell align="right">
                              <Button size="sm" variant="outline" onClick={() => handleOpenUntisLink(row)}>
                                Mit LR verknüpfen
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableScrollArea>
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">LR-Lehrkräfte ohne UNTIS-Kürzel</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Aktive Konten (Lehrkraft, Klassenlehrer, Admin, …) ohne Primär-Kürzel und ohne Eintrag in der
                externen Lehrkraft-Map.
              </p>
              <TableScrollArea>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Lehrkraft</TableHeaderCell>
                      <TableHeaderCell>Benutzername</TableHeaderCell>
                      <TableHeaderCell>Rolle</TableHeaderCell>
                      <TableHeaderCell>E-Mail</TableHeaderCell>
                      {isAdmin && <TableHeaderCell align="right">Aktion</TableHeaderCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teacherMappingsLrUnlinked.length === 0 ? (
                      <EmptyTableRow
                        colSpan={isAdmin ? 5 : 4}
                        title={
                          teacherMappingCounts ? 'Alle LR-Lehrkräfte haben ein UNTIS-Kürzel' : 'Übersicht laden'
                        }
                      />
                    ) : (
                      teacherMappingsLrUnlinked.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            {row.first_name} {row.last_name}
                          </TableCell>
                          <TableCell>{row.username}</TableCell>
                          <TableCell>{lrRoleLabel(row.role)}</TableCell>
                          <TableCell>{row.email || '—'}</TableCell>
                          {isAdmin && (
                            <TableCell align="right">
                              <Button size="sm" variant="outline" onClick={() => handleOpenTeacherEdit(row)}>
                                Kürzel setzen
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableScrollArea>
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Verknüpfte Lehrkräfte</h3>
              <TableScrollArea>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Lehrkraft</TableHeaderCell>
                      <TableHeaderCell>Benutzername</TableHeaderCell>
                      <TableHeaderCell>Rolle</TableHeaderCell>
                      <TableHeaderCell>Primär-Kürzel</TableHeaderCell>
                      <TableHeaderCell>Weitere Kürzel</TableHeaderCell>
                      {isAdmin && <TableHeaderCell align="right">Aktion</TableHeaderCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teacherMappingsLinked.length === 0 ? (
                      <EmptyTableRow
                        colSpan={isAdmin ? 6 : 5}
                        title={teacherMappingCounts ? 'Keine verknüpften Lehrkräfte' : 'Übersicht laden'}
                      />
                    ) : (
                      teacherMappingsLinked.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            {row.first_name} {row.last_name}
                          </TableCell>
                          <TableCell>{row.username}</TableCell>
                          <TableCell>{lrRoleLabel(row.role)}</TableCell>
                          <TableCell className="font-mono text-sm">{row.untis_teacher_code || '—'}</TableCell>
                          <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                            {row.additional_untis_codes && row.additional_untis_codes.length > 0
                              ? row.additional_untis_codes.join(', ')
                              : '—'}
                          </TableCell>
                          {isAdmin && (
                            <TableCell align="right">
                              <Button size="sm" variant="outline" onClick={() => handleOpenTeacherEdit(row)}>
                                Bearbeiten
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableScrollArea>
            </div>
          </div>
        </details>
      )}

      <details className="group overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-950/30">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-gray-900 outline-none ring-primary-500 hover:bg-gray-50 focus-visible:ring-2 dark:text-gray-100 dark:hover:bg-gray-900/60 [&::-webkit-details-marker]:hidden">
          <span>Fach-Kürzel-Aliase (UNTIS)</span>
          <span className="flex items-center gap-2 text-xs font-normal text-gray-500 dark:text-gray-400">
            Optional — Matching für Import
            <ChevronDown
              className="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-180 dark:text-gray-500"
              aria-hidden
            />
          </span>
        </summary>
        <div className="space-y-3 border-t border-gray-200 p-4 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Zusätzliche Kürzel für UNTIS-Matching; reduziert typische{' '}
            <span className="font-mono text-xs">subject_not_found</span>-Fälle.
          </p>
          <div className="grid gap-3 md:grid-cols-[minmax(14rem,1fr)_minmax(14rem,1fr)_auto]">
            <label className="text-sm text-gray-700 dark:text-gray-300">
              Fach
              <select
                className="input-field mt-1"
                value={selectedAliasSubjectId}
                onChange={(event) => setSelectedAliasSubjectId(event.target.value)}
              >
                <option value="">Bitte wählen</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.subject_name}
                    {subject.subject_code ? ` (${subject.subject_code})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-700 dark:text-gray-300">
              Neues Kürzel
              <input
                className="input-field mt-1"
                value={newAliasCode}
                onChange={(event) => setNewAliasCode(event.target.value)}
                placeholder="z. B. LB 5/6"
              />
            </label>
            <div className="flex items-end">
              <Button onClick={handleAddSubjectAlias} disabled={!selectedAliasSubjectId || !newAliasCode.trim()}>
                Alias hinzufügen
              </Button>
            </div>
          </div>

          <TableScrollArea>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Fach</TableHeaderCell>
                  <TableHeaderCell>Standard-Kürzel</TableHeaderCell>
                  <TableHeaderCell>Alias</TableHeaderCell>
                  <TableHeaderCell align="right">Aktion</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subjectAliases.length === 0 ? (
                  <EmptyTableRow colSpan={4} title="Keine Fach-Aliase vorhanden" />
                ) : (
                  subjectAliases.map((alias) => (
                    <TableRow key={alias.id}>
                      <TableCell>{alias.subject_name}</TableCell>
                      <TableCell>{alias.subject_code || '—'}</TableCell>
                      <TableCell>{alias.alias_code}</TableCell>
                      <TableCell align="right">
                        <Button size="sm" variant="outline" onClick={() => handleDeleteSubjectAlias(alias.id)}>
                          Löschen
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableScrollArea>
        </div>
      </details>

      {isAdmin && (
        <details
          className="group overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-950/30"
          onToggle={(event) => {
            if ((event.currentTarget as HTMLDetailsElement).open) void loadAllLocations()
          }}
        >
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-gray-900 outline-none ring-primary-500 hover:bg-gray-50 focus-visible:ring-2 dark:text-gray-100 dark:hover:bg-gray-900/60 [&::-webkit-details-marker]:hidden">
            <span>Standorte verwalten</span>
            <span className="flex items-center gap-2 text-xs font-normal text-gray-500 dark:text-gray-400">
              {allLocations.length > 0 ? `${allLocations.filter((l) => Boolean(l.is_active)).length} aktiv` : 'Anlegen & bearbeiten'}
              <ChevronDown
                className="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-180 dark:text-gray-500"
                aria-hidden
              />
            </span>
          </summary>
          <div className="space-y-3 border-t border-gray-200 p-4 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Standorte für UNTIS-Import, Stundenplan-Filter und standortbezogene Pläne. Inaktive Standorte erscheinen nicht
              in Auswahllisten, bleiben aber an bestehenden Stundenplänen erhalten.
            </p>
            <div className="grid gap-3 md:grid-cols-[minmax(8rem,10rem)_minmax(14rem,1fr)_auto]">
              <label className="text-sm text-gray-700 dark:text-gray-300">
                Kürzel
                <input
                  className="input-field mt-1 font-mono"
                  value={newLocationCode}
                  onChange={(event) => setNewLocationCode(event.target.value)}
                  placeholder="z. B. SIM"
                  maxLength={50}
                />
              </label>
              <label className="text-sm text-gray-700 dark:text-gray-300">
                Name
                <input
                  className="input-field mt-1"
                  value={newLocationName}
                  onChange={(event) => setNewLocationName(event.target.value)}
                  placeholder="z. B. Simmerath"
                  maxLength={255}
                />
              </label>
              <div className="flex items-end">
                <Button
                  onClick={() => void handleCreateLocation()}
                  disabled={locationCreateBusy || !newLocationCode.trim() || !newLocationName.trim()}
                >
                  {locationCreateBusy ? 'Wird angelegt…' : 'Standort anlegen'}
                </Button>
              </div>
            </div>

            <TableScrollArea>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Kürzel</TableHeaderCell>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell align="right">Aktion</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allLocations.length === 0 ? (
                    <EmptyTableRow
                      colSpan={4}
                      title={locationsManageLoading ? 'Standorte werden geladen…' : 'Noch keine Standorte angelegt'}
                    />
                  ) : (
                    allLocations.map((location) => (
                      <TableRow key={location.id}>
                        <TableCell className="font-mono text-sm">{location.code}</TableCell>
                        <TableCell>{location.name}</TableCell>
                        <TableCell>
                          <Badge variant={location.is_active ? 'success' : 'neutral'}>
                            {location.is_active ? 'Aktiv' : 'Inaktiv'}
                          </Badge>
                        </TableCell>
                        <TableCell align="right">
                          <Button size="sm" variant="outline" onClick={() => handleOpenLocationEdit(location)}>
                            Bearbeiten
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableScrollArea>
          </div>
        </details>
      )}

      {isAdmin && (
        <details className="group overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-950/30">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-gray-900 outline-none ring-primary-500 hover:bg-gray-50 focus-visible:ring-2 dark:text-gray-100 dark:hover:bg-gray-900/60 [&::-webkit-details-marker]:hidden">
            <span>Klassenbuch — Systemeinstellungen</span>
            <span className="flex items-center gap-2 text-xs font-normal text-gray-500 dark:text-gray-400">
              Nur Admins
              <ChevronDown
                className="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-180 dark:text-gray-500"
                aria-hidden
              />
            </span>
          </summary>
          <div className="space-y-3 border-t border-gray-200 p-4 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Rahmenzeiten, Stundenraster, Import-Quellen und Fachfarben für Klassenbuch und Stundenplan.
            </p>
            <KlassenbuchSettings />
          </div>
        </details>
      )}

      <Modal
        isOpen={teacherSuggestModalOpen}
        onClose={closeTeacherSuggestModal}
        title="Automatische Zuordnungsvorschläge"
        size="lg"
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="outline"
              disabled={teacherSuggestBusy || teacherSuggestApplyBusy}
              onClick={closeTeacherSuggestModal}
            >
              Schließen
            </Button>
            {teacherSuggestions.some((row) => row.confidence >= TEACHER_SUGGEST_HIGH_CONFIDENCE) && (
              <Button
                variant="outline"
                disabled={teacherSuggestApplyBusy || teacherSuggestBusy}
                onClick={() => {
                  const keys = new Set(
                    teacherSuggestions
                      .filter((row) => row.confidence >= TEACHER_SUGGEST_HIGH_CONFIDENCE)
                      .map((row) => teacherSuggestKey(row))
                  )
                  void applyTeacherSuggestions(keys)
                }}
              >
                Sichere verknüpfen (
                {
                  teacherSuggestions.filter((row) => row.confidence >= TEACHER_SUGGEST_HIGH_CONFIDENCE)
                    .length
                }
                )
              </Button>
            )}
            <Button
              variant="primary"
              disabled={
                teacherSuggestApplyBusy || teacherSuggestBusy || teacherSuggestSelected.size === 0
              }
              onClick={() => void applyTeacherSuggestions(teacherSuggestSelected)}
            >
              {teacherSuggestApplyBusy
                ? 'Speichern…'
                : `Ausgewählte verknüpfen (${teacherSuggestSelected.size})`}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Vorschläge basieren auf LDAP, Nachname = UNTIS-Kürzel und Anzeigenamen. Prüfen Sie unsichere Treffer
            vor dem Speichern.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                checked={teacherSuggestUseHub}
                onChange={(event) => setTeacherSuggestUseHub(event.target.checked)}
                disabled={teacherSuggestBusy || teacherSuggestApplyBusy}
              />
              HUB-Vorschläge bei mehrdeutigen Namen (langsamer, optional)
            </label>
            <Button
              variant="outline"
              size="sm"
              disabled={teacherSuggestBusy || teacherSuggestApplyBusy}
              onClick={() => void runTeacherSuggest(teacherSuggestUseHub)}
            >
              {teacherSuggestBusy ? 'Analysiere…' : 'Erneut analysieren'}
            </Button>
          </div>
          {teacherSuggestCounts && (
            <div className="flex flex-wrap gap-2">
              <Badge variant="info">{teacherSuggestCounts.suggestions} Vorschläge</Badge>
              <Badge variant="success">{teacherSuggestCounts.high_confidence} sicher</Badge>
              {teacherSuggestCounts.unresolved > 0 && (
                <Badge variant="neutral">{teacherSuggestCounts.unresolved} ohne Vorschlag</Badge>
              )}
            </div>
          )}
          {teacherSuggestions.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Keine Vorschläge — ggf. Übersicht laden oder HUB aktivieren und erneut analysieren.
            </p>
          ) : (
            <TableScrollArea>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell className="w-10">
                      <span className="sr-only">Auswahl</span>
                    </TableHeaderCell>
                    <TableHeaderCell>UNTIS</TableHeaderCell>
                    <TableHeaderCell>LR-Konto</TableHeaderCell>
                    <TableHeaderCell>Vertrauen</TableHeaderCell>
                    <TableHeaderCell>Quelle</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teacherSuggestions.map((row) => {
                    const key = teacherSuggestKey(row)
                    const checked = teacherSuggestSelected.has(key)
                    return (
                      <TableRow key={key}>
                        <TableCell>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                            checked={checked}
                            onChange={(event) => toggleTeacherSuggestRow(key, event.target.checked)}
                            disabled={teacherSuggestApplyBusy}
                            aria-label={`${row.teacher_code} mit ${row.username} verknüpfen`}
                          />
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm">{row.teacher_code}</span>
                          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                            {row.teacher_display_name}
                          </span>
                        </TableCell>
                        <TableCell>
                          {row.last_name}, {row.first_name}{' '}
                          <span className="text-gray-500 dark:text-gray-400">({row.username})</span>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              row.confidence >= TEACHER_SUGGEST_HIGH_CONFIDENCE ? 'success' : 'warning'
                            }
                          >
                            {Math.round(row.confidence * 100)} %
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                          {suggestMethodLabel(row.method)}
                          <span className="mt-0.5 block text-xs">{row.reason}</span>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableScrollArea>
          )}
          {teacherSuggestUnresolved.length > 0 && (
            <details className="text-sm text-gray-600 dark:text-gray-400">
              <summary className="cursor-pointer font-medium text-gray-800 dark:text-gray-200">
                {teacherSuggestUnresolved.length} Kürzel ohne Vorschlag
              </summary>
              <ul className="mt-2 list-inside list-disc space-y-1">
                {teacherSuggestUnresolved.slice(0, 20).map((row) => (
                  <li key={row.teacher_code}>
                    <span className="font-mono">{row.teacher_code}</span> — {row.teacher_display_name}
                    {row.candidate_count > 0
                      ? ` (${row.candidate_count} mögliche LR-Konten)`
                      : ''}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={teacherLinkModalOpen}
        onClose={handleCloseTeacherEditModal}
        title={isUntisLinkMode ? 'UNTIS-Lehrkraft mit LR-Konto verknüpfen' : 'UNTIS-Kürzel für Lehrkraft bearbeiten'}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={teacherEditBusy} onClick={handleCloseTeacherEditModal}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              disabled={teacherEditBusy || !teacherEditCode.trim() || (isUntisLinkMode && !teacherLinkUserId)}
              onClick={() => void handleSaveTeacherEdit()}
            >
              {teacherEditBusy ? 'Speichern…' : 'Speichern'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {isUntisLinkMode ? (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                UNTIS-Kürzel{' '}
                <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{teacherEditCode}</span> einem
                LR-Lehrkraft-Konto zuordnen.
              </p>
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                LR-Lehrkraft
                <select
                  className="input-field mt-1"
                  value={teacherLinkUserId}
                  onChange={(event) => setTeacherLinkUserId(event.target.value)}
                >
                  <option value="">Bitte wählen</option>
                  {lrTeachersForLinkSelect.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.last_name}, {row.first_name} ({row.username})
                      {row.untis_teacher_code ? ` — bereits: ${row.untis_teacher_code}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : teacherEditTarget ? (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Lehrkraft:{' '}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {teacherEditTarget.first_name} {teacherEditTarget.last_name} ({teacherEditTarget.username})
                </span>
              </p>
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                UNTIS-Kürzel (Primärcode)
                <input
                  className="input-field mt-1"
                  placeholder="z. B. Mü"
                  value={teacherEditCode}
                  onChange={(event) => setTeacherEditCode(event.target.value)}
                />
              </label>
            </>
          ) : null}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Nach dem Speichern werden offene Stundenplanzeilen mit diesem Kürzel automatisch verknüpft.
          </p>
        </div>
      </Modal>

      <Modal
        isOpen={locationEditTarget !== null}
        onClose={handleCloseLocationEdit}
        title="Standort bearbeiten"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={locationEditBusy} onClick={handleCloseLocationEdit}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              disabled={locationEditBusy || !editLocationCode.trim() || !editLocationName.trim()}
              onClick={() => void handleSaveLocationEdit()}
            >
              {locationEditBusy ? 'Speichern…' : 'Speichern'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {error && locationEditTarget && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
              {error}
            </p>
          )}
          <label className="block text-sm text-gray-700 dark:text-gray-300">
            Kürzel
            <input
              className="input-field mt-1 font-mono"
              value={editLocationCode}
              onChange={(event) => setEditLocationCode(event.target.value)}
              maxLength={50}
            />
          </label>
          <label className="block text-sm text-gray-700 dark:text-gray-300">
            Name
            <input
              className="input-field mt-1"
              value={editLocationName}
              onChange={(event) => setEditLocationName(event.target.value)}
              maxLength={255}
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
              checked={editLocationActive}
              onChange={(event) => setEditLocationActive(event.target.checked)}
              disabled={locationEditBusy}
            />
            Standort ist aktiv (in Auswahllisten sichtbar)
          </label>
        </div>
      </Modal>

      <Modal
        isOpen={timetableEditTarget !== null}
        onClose={handleCloseTimetableEdit}
        title="Stundenplan bearbeiten"
        description={
          timetableEditTarget ? (
            <>
              Metadaten für „{timetableEditTarget.label}“ (v{timetableEditTarget.version_no}) anpassen — z. B. Gültigkeit,
              Standort oder Zeitraster.
            </>
          ) : undefined
        }
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={editTimetableBusy} onClick={handleCloseTimetableEdit}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              disabled={
                editTimetableBusy ||
                !editLabel.trim() ||
                !editSchoolYearId ||
                !editScheduleGridId ||
                !editValidFrom ||
                !editValidUntil
              }
              onClick={() => void handleSaveTimetableEdit()}
            >
              {editTimetableBusy ? 'Speichern…' : 'Speichern'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {error && timetableEditTarget && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
              {error}
            </p>
          )}
          {timetableEditTarget?.status === 'active' && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
              Dieser Stundenplan ist aktiv. Änderungen an Gültigkeit, Standort oder Zeitraster werden auf Überschneidungen
              mit anderen aktiven Plänen geprüft.
            </p>
          )}
          <label className="block text-sm text-gray-700 dark:text-gray-300">
            Bezeichnung
            <input
              className="input-field mt-1"
              value={editLabel}
              onChange={(event) => setEditLabel(event.target.value)}
              placeholder="z. B. UNTIS Import 2026-05"
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-gray-700 dark:text-gray-300">
              Schuljahr
              <select
                className="input-field mt-1"
                value={editSchoolYearId}
                onChange={(event) => setEditSchoolYearId(event.target.value)}
              >
                <option value="">Bitte wählen</option>
                {schoolYears.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-700 dark:text-gray-300">
              Standort
              <select
                className="input-field mt-1"
                value={editLocationId}
                onChange={(event) => setEditLocationId(event.target.value)}
              >
                <option value="">Schulweit (kein Standort)</option>
                {locations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="block text-sm text-gray-700 dark:text-gray-300">
            Zeitraster
            <select
              className="input-field mt-1"
              value={editScheduleGridId}
              onChange={(event) => setEditScheduleGridId(event.target.value)}
            >
              <option value="">Bitte wählen</option>
              {scheduleGrids.map((grid) => (
                <option key={grid.id} value={grid.id}>
                  {grid.name}
                  {grid.id === defaultScheduleGridId ? ' (Standard)' : ''}
                </option>
              ))}
            </select>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-gray-700 dark:text-gray-300">
              Gültig ab
              <input
                type="date"
                className="input-field mt-1"
                value={editValidFrom}
                onChange={(event) => setEditValidFrom(event.target.value)}
              />
            </label>
            <label className="text-sm text-gray-700 dark:text-gray-300">
              Gültig bis
              <input
                type="date"
                className="input-field mt-1"
                value={editValidUntil}
                onChange={(event) => setEditValidUntil(event.target.value)}
              />
            </label>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={timetablePendingDelete !== null}
        onClose={() => {
          if (!deleteTimetableBusy) setTimetablePendingDelete(null)
        }}
        title="Stundenplan löschen?"
        description={
          timetablePendingDelete ? (
            <>
              Der Stundenplan „{timetablePendingDelete.label}“ wird inklusive aller gespeicherten Stunden- und
              Periodendaten unwiderruflich entfernt.
            </>
          ) : undefined
        }
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={deleteTimetableBusy} onClick={() => setTimetablePendingDelete(null)}>
              Abbrechen
            </Button>
            <Button variant="danger" disabled={deleteTimetableBusy} onClick={() => void handleConfirmDeleteTimetable()}>
              {deleteTimetableBusy ? 'Wird gelöscht…' : 'Endgültig löschen'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Aktive Stundenpläne können nicht direkt gelöscht werden — bitte zuerst deaktivieren (archivieren). Löschen ist
          für Entwürfe und archivierte Pläne vorgesehen.
        </p>
      </Modal>

      <Modal
        isOpen={showImportModal}
        onClose={handleCloseImportModal}
        title="UNTIS-Import"
        description="Import von GPU001–GPU006 als versionierter Stundenplan mit Dry-Run, Konfliktauflösung und Aktivierung."
        size="xl"
      >
        <TimetableUntisImportPanel
          schoolYears={schoolYears}
          selectedSchoolYearId={selectedSchoolYearId}
          setSelectedSchoolYearId={setSelectedSchoolYearId}
          locations={locations}
          selectedLocationId={selectedLocationId}
          setSelectedLocationId={setSelectedLocationId}
          label={label}
          setLabel={setLabel}
          scheduleGrids={scheduleGrids}
          defaultScheduleGridId={defaultScheduleGridId}
          selectedScheduleGridId={selectedScheduleGridId}
          setSelectedScheduleGridId={setSelectedScheduleGridId}
          fileFieldLabels={fileFieldLabels}
          handleFileChange={handleFileChange}
          canDryRun={canDryRun}
          untisImportBusy={untisImportBusy}
          sessionUid={sessionUid}
          unresolvedCount={unresolvedCount}
          error={error}
          successMessage={successMessage}
          blockingQuestions={blockingQuestions}
          resolutions={resolutions}
          setResolutions={setResolutions}
          unresolvedCourseNotFoundCount={unresolvedCourseNotFoundCount}
          llmTeacherSuggestionCount={llmTeacherSuggestionCount}
          applyCreateCourseResolutions={applyCreateCourseResolutions}
          applyLlmTeacherSuggestions={applyLlmTeacherSuggestions}
          updateResolution={updateResolution}
          resolutionActionForQuestionType={resolutionActionForQuestionType}
          teachersWithoutAccount={teachersWithoutAccount}
          ldapDryRunHints={ldapDryRunHints}
          ldapCommitSummary={ldapCommitSummary}
          createdCourses={createdCourses}
          onDryRun={handleDryRun}
          onImportDraft={() => handleCommit(false)}
          onImportAndActivate={() => handleCommit(true)}
        />
      </Modal>

      {untisImportBusy && (
        <Modal
          isOpen={untisImportBusy}
          onClose={() => {}}
          closeOnBackdrop={false}
          size="lg"
          title={untisImportMode === 'dry_run' ? 'Dry-Run läuft' : 'UNTIS-Import läuft'}
          description="Bitte warten — Status wird fortlaufend aktualisiert."
          backdropClassName="z-[70]"
          headerActions={<Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden />}
        >
          <div role="status" aria-live="polite" aria-busy="true" className="space-y-2">
            <ul className="space-y-1.5 text-sm text-gray-800 dark:text-gray-200">
              {importStatusLines.map((line, i) => (
                <li key={`${i}-${line.slice(0, 24)}`} className="break-words border-l-2 border-primary-200 pl-2 dark:border-primary-800">
                  {line}
                </li>
              ))}
            </ul>
            <div ref={importLogEndRef} />
          </div>
        </Modal>
      )}
    </div>
  )
}
